import { ListItemButton, ListItemIcon, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowSmallDown, ArrowSmallRight } from '@/assets/icons';
import {
  getGeneratedLayerName,
  isLayerNames,
  isVehicleGeneratedLayerName,
  Layer,
  LayerName,
  LayerNames,
  Layers,
  LayerVisibilityMode,
} from '@/modules/common/types/layers';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { buildTagMatchRegExp } from '@/modules/floorplanDesign/helpers';
import { useCanvasStore } from '@/modules/canvas/store';

import { Label } from './common/Label';
import { useLayerStore } from '../store/useLayerStore';
import { getLayerOfLayerType, sortObjects } from '../helpers';
import { getRelativeLayers } from '../layerHierarchy';
import { AreaItem } from './AreaItem';
import { LayerServiceFactory } from '../service/LayerServiceFactory';
import { getLayerDefinition, isLayerLockable } from '../layerTree';
import { LayerActions } from './LayerActions';
import { useUpdateLayerUserPreference } from '../hooks/useUpdateLayerUserPreference';

export type LayerElement = {
  id: string;
  name?: string;
  type?: string;
};

export type LayerGroup = {
  id: string;
  name?: LayerName;
  label: string;
  elements?: LayerElement[];
  groups?: LayerGroup[];
};

type LayerItemProps = {
  name?: LayerName;
  label: string;
  level: number;
  elements?: LayerElement[];
  groups?: LayerGroup[];
  searchQuery?: string;
};

const EMPTY_ELEMENTS: LayerElement[] = [];
const EMPTY_GROUPS: LayerGroup[] = [];

const includesSearchQuery = (value: string | undefined, searchQuery: string): boolean =>
  Boolean(value?.toLocaleLowerCase().includes(searchQuery));

const layerElementMatchesSearch = ({ id, name }: LayerElement, searchQuery: string): boolean =>
  includesSearchQuery(name, searchQuery) || includesSearchQuery(id, searchQuery);

const layerGroupMatchesSearch = (
  { label, elements = EMPTY_ELEMENTS, groups = EMPTY_GROUPS }: LayerGroup,
  searchQuery: string,
): boolean =>
  includesSearchQuery(label, searchQuery) ||
  elements.some((element) => layerElementMatchesSearch(element, searchQuery)) ||
  groups.some((group) => layerGroupMatchesSearch(group, searchQuery));

const getLayerNamesFromGroups = (groups: readonly LayerGroup[]): LayerName[] =>
  groups.flatMap((group) => [
    ...(group.name ? [group.name] : []),
    ...getLayerNamesFromGroups(group.groups ?? []),
  ]);

const getUniqueLayerNames = (layerNames: readonly LayerName[]): LayerName[] => [
  ...new Set(layerNames),
];

const getControlledLayerNames = ({
  name,
  childLayerNames,
}: {
  name?: LayerName;
  childLayerNames: readonly LayerName[];
}): LayerName[] => {
  if (childLayerNames.length > 0) {
    return getUniqueLayerNames(childLayerNames);
  }

  if (name) {
    return [name];
  }

  return [];
};

const getSharedVisibilityMode = (
  layerNames: readonly LayerName[],
  layers: Layers,
): LayerVisibilityMode | undefined => {
  const visibilityModes = layerNames
    .map((layerName) => {
      const generatedLayerName = getGeneratedLayerName(layerName);

      return (
        layers[layerName]?.visibilityMode ??
        (generatedLayerName ? layers[generatedLayerName]?.visibilityMode : undefined)
      );
    })
    .filter((visibilityMode): visibilityMode is LayerVisibilityMode => Boolean(visibilityMode));

  const firstVisibilityMode = visibilityModes[0];
  if (!firstVisibilityMode) return undefined;

  const allModesMatch = visibilityModes.every(
    (visibilityMode) => visibilityMode === firstVisibilityMode,
  );

  if (allModesMatch) return firstVisibilityMode;

  return undefined;
};

const getLayerIsVisible = (layerNames: readonly LayerName[], layers: Layers): boolean =>
  layerNames.every((layerName) => {
    const generatedLayerName = getGeneratedLayerName(layerName);
    const visibilityMode =
      layers[layerName]?.visibilityMode ??
      (generatedLayerName ? layers[generatedLayerName]?.visibilityMode : undefined);

    return visibilityMode !== 'force_hide';
  });

const getLayerState = (layerName: LayerName, layers: Layers): Layer | undefined => {
  const layer = layers[layerName];
  if (layer) return layer;

  const generatedLayerName = getGeneratedLayerName(layerName);
  if (!generatedLayerName) return undefined;

  return layers[generatedLayerName];
};

const isLockableLayerName = (layerName: LayerName): layerName is LayerNames =>
  isLayerNames(layerName) && isLayerLockable(layerName);

const layerHasLod = (layerName: LayerName): boolean => {
  const generatedLayerName = getGeneratedLayerName(layerName);

  if (generatedLayerName) {
    return Boolean(getLayerDefinition(generatedLayerName)?.lod);
  }

  if (!isLayerNames(layerName)) return false;

  return Boolean(getLayerDefinition(layerName)?.lod);
};

const LayerItemComponent = ({
  name,
  label,
  level,
  elements = EMPTY_ELEMENTS,
  groups = EMPTY_GROUPS,
  searchQuery = '',
}: LayerItemProps) => {
  const layers = useLayerStore((state) => state.layers);
  const layerContainsSelectedShapes = useLayerStore((state) => state.layerContainsSelectedShapes);
  const { updateVisibilityModes } = useUpdateLayerUserPreference();
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const layerSvc = useMemo(() => LayerServiceFactory.getInstance().createLayerService(), []);

  const labelMatchesSearch = searchQuery.length > 0 && includesSearchQuery(label, searchQuery);

  const visibleElements = useMemo(() => {
    if (!searchQuery || labelMatchesSearch) return elements;

    return elements.filter((element) => layerElementMatchesSearch(element, searchQuery));
  }, [elements, labelMatchesSearch, searchQuery]);

  const visibleGroups = useMemo(() => {
    if (!searchQuery || labelMatchesSearch) return groups;

    return groups.filter((group) => layerGroupMatchesSearch(group, searchQuery));
  }, [groups, labelMatchesSearch, searchQuery]);

  const childSearchQuery = labelMatchesSearch ? '' : searchQuery;

  const hasSearchMatch =
    !searchQuery || labelMatchesSearch || visibleElements.length > 0 || visibleGroups.length > 0;

  const childLayerNames = useMemo(() => getLayerNamesFromGroups(groups), [groups]);

  const controlledLayerNames = useMemo(
    () =>
      getControlledLayerNames({
        name,
        childLayerNames,
      }),
    [childLayerNames, name],
  );

  const lockActionLayerNames = useMemo(
    () => controlledLayerNames.filter(isLockableLayerName),
    [controlledLayerNames],
  );

  const selected = useMemo(
    () =>
      controlledLayerNames.some(
        (layerName) => isLayerNames(layerName) && layerContainsSelectedShapes(layerName),
      ),
    [controlledLayerNames, layerContainsSelectedShapes],
  );

  const layerIsVisible = useMemo(
    () => getLayerIsVisible(controlledLayerNames, layers),
    [controlledLayerNames, layers],
  );

  const layerIsLocked = useMemo(
    () =>
      lockActionLayerNames.length > 0 &&
      lockActionLayerNames.every((layerName) => getLayerState(layerName, layers)?.locked),
    [lockActionLayerNames, layers],
  );

  const layerVisibilityMode = useMemo(
    () => getSharedVisibilityMode(controlledLayerNames, layers),
    [controlledLayerNames, layers],
  );

  const showAuto = useMemo(() => controlledLayerNames.some(layerHasLod), [controlledLayerNames]);

  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);

  const removeLayersFromSelection = useCallback(
    (layersToRemove: LayerName[]) => {
      if (!selected) return;

      const staticLayersToRemove = layersToRemove.filter(isLayerNames);
      if (staticLayersToRemove.length === 0) return;

      const canvas = useCanvasStore.getState()?.instance;
      const { selectedIds } = useAppStore.getState();
      const selectedIdSet = new Set(selectedIds);
      const { design, uxonly } = useFloorplanDesignStore.getState();
      const elementStates = sortObjects(design, uxonly);
      const selectedElements = elementStates.filter((shape) => selectedIdSet.has(shape.id));
      const relatedLayers = staticLayersToRemove.flatMap((layer) => [
        layer,
        ...getRelativeLayers(layer),
      ]);

      const canvasIdsToRemove = canvas
        ? relatedLayers.flatMap((layer) =>
            canvas.getElementsByTag(buildTagMatchRegExp([layer])).map((element) => element.id),
          )
        : [];

      const shapeIdsToRemove = selectedElements
        .filter((selectedElement) => {
          const elementLayer = getLayerOfLayerType(selectedElement.type);
          if (!elementLayer) return false;

          return relatedLayers.includes(elementLayer);
        })
        .map((selectedElement) => selectedElement.id);

      const idsToRemove = new Set([...canvasIdsToRemove, ...shapeIdsToRemove]);
      const newSelectedIds = selectedIds.filter((selectedId) => !idsToRemove.has(selectedId));

      appSvc.setSelectedIds(newSelectedIds);
    },
    [appSvc, selected],
  );

  const toggleLock = useCallback(() => {
    const nextLocked = !layerIsLocked;

    lockActionLayerNames.forEach((layerName) => {
      layerSvc.setLayerLock(layerName, nextLocked);
    });
  }, [layerIsLocked, layerSvc, lockActionLayerNames]);

  const setLayerVisibilityMode = useCallback(
    (visibilityMode: LayerVisibilityMode) => {
      if (controlledLayerNames.length === 0) return;

      controlledLayerNames.forEach((layerName) => {
        if (isVehicleGeneratedLayerName(layerName)) {
          useLayerStore
            .getState()
            .setVehicleGeneratedLayerVisibilityMode(layerName, visibilityMode);
          return;
        }

        layerSvc.setLayerVisibilityMode(layerName, visibilityMode);
      });

      updateVisibilityModes(controlledLayerNames.filter(isLayerNames), visibilityMode);
    },
    [controlledLayerNames, layerSvc, updateVisibilityModes],
  );

  const startHover = useCallback(() => {
    setHover(true);
  }, []);

  const endHover = useCallback(() => {
    setHover(false);
  }, []);

  const handleClick = useCallback(() => {
    if (searchQuery) return;

    setOpen((currentOpen) => !currentOpen);
  }, [searchQuery]);

  useEffect(() => {
    if (controlledLayerNames.length === 0) return;
    if (!layerIsLocked && layerIsVisible) return;

    const layersToRemove = layerIsVisible ? lockActionLayerNames : controlledLayerNames;
    if (layersToRemove.length === 0) return;

    removeLayersFromSelection(layersToRemove);
  }, [
    controlledLayerNames,
    layerIsLocked,
    layerIsVisible,
    lockActionLayerNames,
    removeLayersFromSelection,
  ]);

  const containerStyle = useMemo(
    () => ({
      paddingLeft: 1 + 2 * level,
      color: !layerIsVisible ? 'primary.light' : 'primary.main',
      backgroundColor: 'shades.light',
      borderColor: 'shades.light',

      '&:hover': {
        backgroundColor: 'shades.light',
      },
    }),
    [layerIsVisible, level],
  );

  if (!hasSearchMatch) return null;

  const hasChildren = visibleElements.length > 0 || visibleGroups.length > 0;
  const isOpen = open || searchQuery.length > 0;
  const showLayerActions = controlledLayerNames.length > 0 && hover;
  const showLock = lockActionLayerNames.length > 0;

  return (
    <>
      <ListItemButton
        alignItems='center'
        onMouseOver={startHover}
        onMouseLeave={endHover}
        disableRipple
        sx={containerStyle}
      >
        <ListItemIcon
          onClick={handleClick}
          sx={{
            minWidth: (theme) => theme.spacing(3),
            fontSize: 24,
          }}
        >
          {hasChildren && isOpen && <ArrowSmallDown data-testid='Arrow Small Down' />}
          {hasChildren && !isOpen && <ArrowSmallRight data-testid='Arrow Small Right' />}
        </ListItemIcon>

        <Label labelText={label} />

        {showLayerActions && (
          <LayerActions
            isLocked={layerIsLocked}
            showLock={showLock}
            visibilityMode={layerVisibilityMode}
            showAuto={showAuto}
            onToggleLock={toggleLock}
            onVisibilityModeChange={setLayerVisibilityMode}
          />
        )}
      </ListItemButton>

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <List component='div' disablePadding>
          {visibleElements.map((element) => (
            <AreaItem
              key={element.id}
              label={element.name}
              id={element.id}
              level={level + 1}
              canBeLocked={showLock}
            />
          ))}

          {visibleGroups.map((group) => (
            <LayerItem
              key={group.id}
              name={group.name}
              level={level + 1}
              label={group.label}
              elements={group.elements}
              groups={group.groups}
              searchQuery={childSearchQuery}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export const LayerItem = memo(LayerItemComponent);
